import { useCallback } from 'react';
import {
	ConvoAIErrorMessage,
	ConvoAIResponseState,
	type ConvoAIStreamingState,
	useConvoAIStreaming,
} from '@atlassian/ai-agent-streaming';
import { expVal } from '@atlassian/jira-feature-experiments';

import { sendExperienceAnalytics } from '@atlassian/jira-issue-view-analytics/src/controllers/send-experience-analytics/index.tsx';
import { JIRA_SOFTWARE } from '@atlassian/jira-shared-types/src/application.tsx';
import { PREMIUM_EDITION } from '@atlassian/jira-shared-types/src/edition.tsx';
import { useNativeLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import { useIssueId } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useSmartSummarizeCommentsAnalytics } from '../../common/analytics/index.tsx';

// The status code belongs to this list won't be sent as taskFail
const STATUS_CODE_RELIABILITY_WHITE_LIST = [400, 401, 403, 429];

export type StreamingRequest = {
	ai_feature_input: {
		content_ari: string;
		locale: string;
	};
};

export type StreamingResponse = {
	content: string;
};

export type StreamingError = {
	message_template: ConvoAIErrorMessage;
	content: string;
	status_code: number;
};

const sendSloMetrics = (
	wasExperienceSuccesful: boolean,
	errorMessage?: string,
	statusCode?: number,
) => {
	const experience = 'jiraSmartSummarizeComments';
	const analyticsSource = 'requestSummaryQuery';
	const additionalAttributes = {
		...(errorMessage && { error: errorMessage }),
		...(statusCode && { statusCode }),
	};
	sendExperienceAnalytics({
		experience,
		wasExperienceSuccesful,
		analyticsSource,
		application: JIRA_SOFTWARE,
		edition: PREMIUM_EDITION,
		additionalAttributes,
	});
};

export const useSmartSummaryStreaming = () => {
	const issueKey = useIssueId();
	const locale = useNativeLocale();
	const { fireTrack } = useSmartSummarizeCommentsAnalytics();
	const cloudId = useCloudId();

	const streamingRequest = {
		ai_feature_input: {
			content_ari: `ari:cloud:jira:${cloudId}:issue/${issueKey}`,
			locale,
		},
	};

	const streamingConfig = {
		apiPath: 'v2/ai-feature/jira/comment-summary/stream',
		timeoutDelay: 60000,
		headers: {
			'x-experience-id': 'jira-smart-summary',
			'x-product': 'jira',
		},
		...(expVal('jira_comment_summary_follow_ups_experiment', 'isEnabled', false)
			? {
					enableFollowUp: true,
				}
			: {
					enableFollowUp: false,
				}),
	};

	const onStateChange = useCallback(
		(state: ConvoAIStreamingState<StreamingResponse>) => {
			// TODO: add potential analytics/peformance markers and potential state changes for ui?
			switch (state.responseState) {
				case ConvoAIResponseState.Initialization:
					break;
				case ConvoAIResponseState.AnswerPart:
					break;
				case ConvoAIResponseState.FinalResponse:
					fireTrack('aiResult viewed', 'jiraSmartSummarizeComments');
					sendSloMetrics(true);
					break;
				case ConvoAIResponseState.Error: {
					const errorMessage = state.error?.message.content;
					const errorStatusCode = state.error?.message.status_code || 500;
					const errorMessageTemplate = state.error?.message.message_template;
					// this is user content, we do not classify this as an error for both SLO and unified error analytics
					if (errorMessageTemplate === ConvoAIErrorMessage.ACCEPTABLE_USE_VIOLATIONS) {
						break;
					}

					/**
					 * Do not trigger SLO failures if they are part of these status code white lists
					 */
					if (!STATUS_CODE_RELIABILITY_WHITE_LIST.includes(errorStatusCode)) {
						sendSloMetrics(false, errorMessage, errorStatusCode);
					}

					fireTrack('aiResult error', 'jiraSmartSummarizeCommentsError', {
						error: errorMessage,
						statusCode: errorStatusCode,
					});
					break;
				}
				default:
					break;
			}
		},
		[fireTrack],
	);

	const [state, refetch] = useConvoAIStreaming<StreamingRequest, StreamingResponse, StreamingError>(
		streamingRequest,
		streamingConfig,
		onStateChange,
	);

	return [state, refetch] as const;
};
